import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Loader2 } from "lucide-react";
import { createPortal } from "react-dom";
import { Link, useParams } from "react-router-dom";

import { MrpSkuHoverPeek, useDeferredMrpSkuHoverHints } from "@/components/mrp/MrpSkuHoverPeek";
import { InventoryBoxCurrentLocationBadges } from "@/components/inventory/InventoryBoxCurrentLocationBadges";
import { InboundContinueStationLink } from "@/pages/shipping/InboundContinueStationLink";
import { hubNestedModalOverlayProps } from "@/components/ui/HubModalOverlay";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { Input, Label } from "@/components/ui/Input";
import { HubPageTitle } from "@/components/HubPageTitle";
import { CardTitleWithHelp } from "@/components/ui/title-help";
import { useHubToast } from "@/components/ui/HubToast";
import { InventoryAmlMpnPicker } from "@/components/inventory/InventoryAmlMpnPicker";
import {
  inventoryBoxSavedMpnLabel,
  inventoryMpnSelectionSatisfied,
  resolveInventoryApprovedComponentId,
} from "@/lib/inventoryAmlMpn";
import { ApiError } from "@/lib/api";
import {
  InventoryBoxConsumeCard,
  InventoryBoxReleaseToFloorCard,
} from "@/components/inventory/InventoryBoxConsumeReleaseCards";
import { InventoryBoxSplitCombineModal } from "@/components/inventory/InventoryBoxTransferCards";
import {
  fetchInventoryBoxAmlAlternates,
  fetchInventoryBoxWithAml,
  fetchInventoryBoxEvents,
  fetchInventoryBoxPendingAdjustment,
  inventoryBoxDescriptionLine,
  inventoryBoxShortLabel,
  reprintInventoryBox,
  saveInventoryBoxQuantity,
  type InventoryBoxEvent,
} from "@/lib/inventoryBoxApi";
import { buildMrpSkuCatalogLineDescription } from "@/lib/mrp";
import { cn } from "@/lib/utils";
import { inventoryBoxEventSummary } from "@/lib/inventoryBoxEventDisplay";
import { InboundZplPrinterHeaderButton } from "@/pages/shipping/InboundZplPrinterHeaderButton";
import { InventoryBoxPutawayCard } from "@/pages/shipping/InventoryBoxPutawayCard";
import { useInboundZplPrinter } from "@/pages/shipping/inboundZplPrinter";

type CartonAdjustAction = "consume" | "release" | "split" | "combine" | "sublocation" | "adjust";

function CartonAdjustActionModal(props: {
  titleId: string;
  title: string;
  pending: boolean;
  onClose: () => void;
  children: ReactNode;
  primaryLabel: string;
  primaryDisabled: boolean;
  onPrimary: () => void;
}): JSX.Element {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || props.pending) return;
      event.preventDefault();
      props.onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [props.onClose, props.pending]);

  return createPortal(
    <div {...hubNestedModalOverlayProps(true)}>
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby={props.titleId}
        className="flex max-h-[calc(100dvh-2rem)] w-full max-w-3xl flex-col overflow-hidden rounded-lg bg-slate-50 shadow-xl"
      >
        <header className="shrink-0 border-b border-slate-200 bg-white px-4 py-3">
          <h2 id={props.titleId} className="text-lg font-semibold text-slate-900">
            {props.title}
          </h2>
        </header>
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">{props.children}</div>
        <footer className="flex shrink-0 justify-end gap-2 border-t border-slate-200 bg-white px-4 py-3">
          <Button type="button" disabled={props.primaryDisabled} onClick={props.onPrimary}>
            {props.pending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
            {props.primaryLabel}
          </Button>
          <Button type="button" variant="secondary" disabled={props.pending} onClick={props.onClose}>
            Cancel
          </Button>
        </footer>
      </section>
    </div>,
    document.body,
  );
}

function formatEventWhen(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function InventoryBoxAdjustPage(): JSX.Element {
  const { boxId = "" } = useParams();
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();
  const [printerPopoverOpen, setPrinterPopoverOpen] = useState(false);
  const [activeAction, setActiveAction] = useState<CartonAdjustAction | null>(null);
  const fetchZplPrinters =
    printerPopoverOpen || activeAction === "split" || activeAction === "adjust";
  const { station, setStation, selectedPrinter, unavailableMessage } = useInboundZplPrinter({
    fetchPrintersEnabled: fetchZplPrinters,
  });
  const boxQuery = useQuery({
    queryKey: ["inventory-box", boxId, "lite"],
    queryFn: () => fetchInventoryBoxWithAml(boxId, { omitAml: true, omitPending: true }),
    enabled: boxId.trim() !== "",
    staleTime: 30_000,
  });
  const pendingQuery = useQuery({
    queryKey: ["inventory-box-pending", boxId],
    queryFn: () => fetchInventoryBoxPendingAdjustment(boxId),
    enabled: boxId.trim() !== "" && boxQuery.isSuccess,
    staleTime: 15_000,
  });
  const needsAmlAlternates = activeAction === "adjust";
  const amlQuery = useQuery({
    queryKey: ["inventory-box-aml", boxId],
    queryFn: () => fetchInventoryBoxAmlAlternates(boxId),
    enabled: boxId.trim() !== "" && boxQuery.isSuccess && needsAmlAlternates,
    staleTime: 60_000,
  });
  const [historyLoadReady, setHistoryLoadReady] = useState(false);
  useEffect(() => {
    if (!boxQuery.isSuccess) {
      setHistoryLoadReady(false);
      return;
    }
    const idleWindow = window as Window & {
      requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number;
      cancelIdleCallback?: (handle: number) => void;
    };
    if (idleWindow.requestIdleCallback != null) {
      const handle = idleWindow.requestIdleCallback(() => setHistoryLoadReady(true), { timeout: 1500 });
      return () => idleWindow.cancelIdleCallback?.(handle);
    }
    const timeout = window.setTimeout(() => setHistoryLoadReady(true), 200);
    return () => window.clearTimeout(timeout);
  }, [boxQuery.isSuccess, boxId]);
  const eventsQuery = useQuery({
    queryKey: ["inventory-box-events", boxId],
    queryFn: () => fetchInventoryBoxEvents(boxId),
    enabled: boxId.trim() !== "" && historyLoadReady,
    staleTime: 30_000,
  });
  const box = boxQuery.data?.box ?? null;
  const cartonSkuHints = useDeferredMrpSkuHoverHints(box != null ? [box.sku] : [], "inventory-carton-adjust");
  const pendingRequest = pendingQuery.data?.pending_adjustment_request ?? null;
  const amlAlternates = amlQuery.data?.aml_mpn_alternates ?? [];
  const amlLoading = needsAmlAlternates && amlQuery.isLoading;
  const events = eventsQuery.data ?? [];
  const archived = box?.archived_at != null;

  const [quantityDraft, setQuantityDraft] = useState("");
  const [reasonDraft, setReasonDraft] = useState("");
  const [transferPending, setTransferPending] = useState(false);
  const actionSubmitRef = useRef({ submit: () => {}, canSubmit: false, pending: false });
  const [actionSubmitReady, setActionSubmitReady] = useState({ canSubmit: false, pending: false });
  const onActionSubmitApi = useCallback((api: { submit: () => void; canSubmit: boolean; pending: boolean }) => {
    actionSubmitRef.current = api;
    setActionSubmitReady((previous) => (
      previous.canSubmit === api.canSubmit && previous.pending === api.pending
        ? previous
        : { canSubmit: api.canSubmit, pending: api.pending }
    ));
  }, []);
  const [consumeReleasePending, setConsumeReleasePending] = useState(false);
  const [approvedComponentIdDraft, setApprovedComponentIdDraft] = useState<string | null>(null);

  useEffect(() => {
    if (box != null) {
      setQuantityDraft(String(box.quantity));
      setApprovedComponentIdDraft(
        resolveInventoryApprovedComponentId({
          alternates: amlAlternates,
          savedApprovedComponentId: box.approved_component_id,
          draftApprovedComponentId: null,
        }),
      );
    }
  }, [box, amlAlternates]);

  const invalidateBox = () => {
    void queryClient.invalidateQueries({ queryKey: ["inventory-box", boxId] });
    void queryClient.invalidateQueries({ queryKey: ["inventory-box-pending", boxId] });
    void queryClient.invalidateQueries({ queryKey: ["inventory-box-aml", boxId] });
    void queryClient.invalidateQueries({ queryKey: ["inventory-box-events", boxId] });
    void queryClient.invalidateQueries({ queryKey: ["manufacturing", "inventory-discrepancy"] });
  };

  const saveMutation = useMutation({
    mutationFn: (quantity: number) =>
      saveInventoryBoxQuantity(boxId, quantity, reasonDraft, approvedComponentIdDraft),
    onSuccess: (result) => {
      invalidateBox();
      setActiveAction(null);
      if (result.pending_adjustment_request != null) {
        showToast({
          message: `Submitted for approval (${String(result.pending_adjustment_request.quantity_before)} to ${String(result.pending_adjustment_request.quantity_after)}).`,
          tone: "success",
        });
      } else {
        showToast({ message: `Quantity saved as ${String(result.box.quantity)}.`, tone: "success" });
      }
    },
    onError: (error) => {
      showToast({
        message: error instanceof ApiError ? error.message : String(error),
        tone: "error",
      });
    },
  });

  const reprintMutation = useMutation({
    mutationFn: () => {
      const printer = selectedPrinter.trim();
      if (printer === "") {
        throw new Error("Pick a label printer first.");
      }
      return reprintInventoryBox(boxId, printer);
    },
    onSuccess: () => {
      invalidateBox();
      showToast({ message: "Label sent to the printer.", tone: "success" });
    },
    onError: (error) => {
      showToast({
        message: error instanceof ApiError ? error.message : String(error),
        tone: "error",
      });
    },
  });

  const parsedQuantity = Number.parseInt(quantityDraft.trim(), 10);
  const quantityValid = Number.isFinite(parsedQuantity) && parsedQuantity >= 0;
  const reasonValid = reasonDraft.trim() !== "";
  const quantityChanged = box != null && quantityValid && parsedQuantity !== box.quantity;
  const mpnReady = inventoryMpnSelectionSatisfied({
    alternates: amlAlternates,
    approvedComponentId: approvedComponentIdDraft,
  });
  const catalogDescription =
    box != null
      ? buildMrpSkuCatalogLineDescription(cartonSkuHints.skuHoverHints?.[box.sku.trim()])
      : null;
  const description =
    box != null
      ? inventoryBoxDescriptionLine(box) || catalogDescription || ""
      : "";
  const savedMpnLabel =
    box != null
      ? (box.saved_mpn_label?.trim() || inventoryBoxSavedMpnLabel(box, amlAlternates))
      : null;
  const busy =
    saveMutation.isPending
    || reprintMutation.isPending
    || consumeReleasePending
    || transferPending;
  const actionButtons: { id: CartonAdjustAction; label: string }[] = [
    { id: "consume", label: "Consume" },
    { id: "release", label: "Release to floor" },
    { id: "split", label: "Split" },
    { id: "combine", label: "Combine" },
    { id: "sublocation", label: "Set sublocation" },
    { id: "adjust", label: "Recount / adjust" },
  ];
  const pageTitle = box != null ? inventoryBoxShortLabel(box.short_code) : "Inventory carton";

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
          <HubPageTitle breadcrumbLabel={pageTitle} className="min-w-0">
            {pageTitle}
          </HubPageTitle>
          {box != null && (box.receiving_stage === "holding" || box.receiving_stage === "to_stock") ? (
            <InboundContinueStationLink
              placement={box.receiving_stage === "holding" ? "holding" : "pending_put_away"}
              inventoryBoxHex={box.hex}
            />
          ) : null}
          {box != null
            && box.receiving_stage !== "holding"
            && box.receiving_stage !== "to_stock"
            && box.kitting_pick != null ? (
            <Link
              className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-800 hover:underline"
              to={`/manufacturing/kitting/pick?run=${String(box.kitting_pick.run_id)}`}
            >
              Picking
            </Link>
          ) : null}
          {box != null && box.receiving_stage !== "holding" && box.receiving_stage !== "to_stock" ? (
            <InventoryBoxCurrentLocationBadges box={box} />
          ) : null}
        </div>
        <InboundZplPrinterHeaderButton
          station={station}
          onStationChange={setStation}
          unavailableMessage={unavailableMessage}
          onPopoverOpenChange={setPrinterPopoverOpen}
        />
      </header>

      {boxQuery.isLoading ? (
        <p className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading carton.
        </p>
      ) : null}

      {boxQuery.error != null ? (
        <p role="alert" className="flex items-center gap-2 text-sm text-red-600">
          <AlertTriangle className="h-4 w-4" />
          {boxQuery.error instanceof ApiError ? boxQuery.error.message : "Inventory carton not found."}
        </p>
      ) : null}

      {pendingRequest != null ? (
        <Card className="border-amber-200 bg-amber-50">
          <CardBody className="space-y-1 text-sm text-amber-900">
            <p className="font-medium">
              {pendingRequest.request_kind === "consume"
                ? "Pending consume approval (Megaventory)"
                : "Pending quantity approval"}
            </p>
            <p>
              {String(pendingRequest.quantity_before)} to {String(pendingRequest.quantity_after)} submitted by{" "}
              {pendingRequest.requested_by_display_name?.trim() || "operator"}.
            </p>
            <p>
              {pendingRequest.request_kind === "consume" ? "Via consume. " : ""}
              Reason: {pendingRequest.reason}
            </p>
          </CardBody>
        </Card>
      ) : null}

      {box != null ? (
        <Card>
          <CardBody className="space-y-2 text-sm text-slate-700">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 flex-1 space-y-1">
                <MrpSkuHoverPeek
                  sku={box.sku}
                  tab="inventory"
                  hint={cartonSkuHints.skuHoverHints?.[box.sku.trim()]}
                  hintsLoading={cartonSkuHints.hintsLoading}
                  onHoverIntent={cartonSkuHints.enableHintsFetch}
                  linkClassName="text-lg font-semibold text-sky-700 hover:text-sky-900"
                />
                {savedMpnLabel != null ? (
                  <p className="font-mono text-sm text-slate-700">{savedMpnLabel}</p>
                ) : null}
                {description !== "" ? (
                  <p className="text-sm leading-snug text-slate-600">{description}</p>
                ) : null}
              </div>
              <div className="shrink-0 text-right leading-none">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Qty</p>
                <p className="text-3xl font-bold tabular-nums text-slate-900">{String(box.quantity)}</p>
              </div>
            </div>
            {box.kitting_pick != null ? (
              <p className="text-amber-800">
                Mid-pick on run {String(box.kitting_pick.run_id)}, cart bin {box.kitting_pick.slot_key}.
                Still in the warehouse until it is delivered. Split is still available.
              </p>
            ) : null}
            {archived && box.combined_into_hex != null ? (
              <p className="text-amber-800">
                Combined into{" "}
                <Link className="underline" to={`/i/${box.combined_into_hex}`}>
                  {box.combined_into_hex.slice(0, 4).toUpperCase()}-{box.combined_into_hex.slice(4, 8).toUpperCase()}
                </Link>
                . This sticker should come off.
              </p>
            ) : null}
          </CardBody>
        </Card>
      ) : null}

      {box != null && !archived ? (
        <div className="grid grid-cols-3 gap-2">
          {actionButtons.map((action) => (
            <button
              key={action.id}
              type="button"
              disabled={busy || (pendingRequest != null && action.id !== "adjust")}
              onClick={() => {
                setActionSubmitReady({ canSubmit: false, pending: false });
                setActiveAction(action.id);
              }}
              className={cn(
                "flex min-h-[5.25rem] w-full items-center justify-center rounded-xl border px-2 py-3 text-center text-sm font-semibold leading-snug touch-manipulation",
                activeAction === action.id
                  ? "border-brand-500 bg-brand-50 text-brand-900"
                  : "border-slate-200 bg-white text-slate-800 hover:border-brand-300 hover:bg-brand-50/40",
              )}
            >
              {action.label}
            </button>
          ))}
        </div>
      ) : null}

      {box != null && activeAction === "consume" && !archived ? (
        <InventoryBoxConsumeCard
          box={box}
          disabled={saveMutation.isPending || reprintMutation.isPending || transferPending}
          onChanged={invalidateBox}
          onPendingChange={setConsumeReleasePending}
          onCompleted={() => setActiveAction(null)}
        />
      ) : null}

      {box != null && activeAction === "release" && !archived ? (
        <CartonAdjustActionModal
          titleId="inventory-box-release-title"
          title={`Release to floor ${inventoryBoxShortLabel(box.short_code)}`}
          pending={actionSubmitReady.pending || consumeReleasePending}
          onClose={() => setActiveAction(null)}
          primaryLabel="Release to station"
          primaryDisabled={!actionSubmitReady.canSubmit || actionSubmitReady.pending}
          onPrimary={() => actionSubmitRef.current.submit()}
        >
          <InventoryBoxReleaseToFloorCard
            box={box}
            plain
            disabled={saveMutation.isPending || reprintMutation.isPending || transferPending}
            onChanged={invalidateBox}
            onPendingChange={setConsumeReleasePending}
            onSubmitApi={onActionSubmitApi}
            onCompleted={() => setActiveAction(null)}
          />
        </CartonAdjustActionModal>
      ) : null}

      {box != null && (activeAction === "split" || activeAction === "combine") && !archived ? (
        <InventoryBoxSplitCombineModal
          open
          mode={activeAction}
          boxHex={box.hex}
          boxShortCode={box.short_code}
          unitKind={box.unit_kind}
          quantity={box.quantity}
          selectedPrinter={selectedPrinter}
          station={station}
          onStationChange={setStation}
          unavailableMessage={unavailableMessage}
          onChanged={invalidateBox}
          onPendingChange={setTransferPending}
          onClose={() => setActiveAction(null)}
        />
      ) : null}

      {box != null && activeAction === "sublocation" && !archived ? (
        <CartonAdjustActionModal
          titleId="inventory-box-sublocation-title"
          title={`Set sublocation ${inventoryBoxShortLabel(box.short_code)}`}
          pending={actionSubmitReady.pending}
          onClose={() => setActiveAction(null)}
          primaryLabel="Save sublocation"
          primaryDisabled={!actionSubmitReady.canSubmit || actionSubmitReady.pending}
          onPrimary={() => actionSubmitRef.current.submit()}
        >
          <InventoryBoxPutawayCard
            box={box}
            plain
            disabled={busy || pendingRequest != null}
            onSubmitApi={onActionSubmitApi}
            onSaved={() => setActiveAction(null)}
          />
        </CartonAdjustActionModal>
      ) : null}

      {box != null && activeAction === "adjust" ? (
        <CartonAdjustActionModal
          titleId="inventory-box-adjust-title"
          title={`Recount / adjust ${inventoryBoxShortLabel(box.short_code)}`}
          pending={saveMutation.isPending}
          onClose={() => setActiveAction(null)}
          primaryLabel="Submit for approval"
          primaryDisabled={
            !quantityValid
            || !reasonValid
            || !quantityChanged
            || !mpnReady
            || busy
            || archived
            || pendingRequest != null
          }
          onPrimary={() => saveMutation.mutate(parsedQuantity)}
        >
          <div className="space-y-4">
            {amlLoading ? (
              <p className="flex items-center gap-2 text-sm text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading manufacturer part numbers.
              </p>
            ) : (
              <InventoryAmlMpnPicker
                alternates={amlAlternates}
                selectedApprovedComponentId={approvedComponentIdDraft}
                disabled={archived || busy || pendingRequest != null}
                onSelect={setApprovedComponentIdDraft}
              />
            )}
            <div>
              <Label htmlFor="inventory-box-quantity">Quantity</Label>
              <Input
                id="inventory-box-quantity"
                className="text-base"
                type="text"
                inputMode="numeric"
                autoComplete="off"
                value={quantityDraft}
                disabled={archived || busy || pendingRequest != null}
                onChange={(event) => setQuantityDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (
                    event.key === "Enter"
                    && quantityValid
                    && reasonValid
                    && quantityChanged
                    && mpnReady
                    && !busy
                    && !archived
                    && pendingRequest == null
                  ) {
                    event.preventDefault();
                    saveMutation.mutate(parsedQuantity);
                  }
                }}
              />
            </div>
            <div>
              <Label htmlFor="inventory-box-reason">Reason</Label>
              <Input
                id="inventory-box-reason"
                className="text-base"
                type="text"
                autoComplete="off"
                placeholder="Why is this quantity changing?"
                value={reasonDraft}
                disabled={archived || busy || pendingRequest != null}
                onChange={(event) => setReasonDraft(event.target.value)}
              />
            </div>
            <Button
              type="button"
              variant="secondary"
              disabled={busy || archived || selectedPrinter.trim() === ""}
              onClick={() => reprintMutation.mutate()}
            >
              {reprintMutation.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
              Reprint label
            </Button>
          </div>
        </CartonAdjustActionModal>
      ) : null}

      {box != null ? (
        <Card className="flex min-h-0 flex-col">
          <CardHeader>
            <CardTitleWithHelp hoverHint="Created, approved quantity saves, reprints, splits, and combines for this container. Approved quantity saves post a Megaventory inventory adjustment for the difference.">
              History
            </CardTitleWithHelp>
          </CardHeader>
          <CardBody className="min-h-0">
            {eventsQuery.isLoading ? (
              <p className="flex items-center gap-2 text-sm text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading history.
              </p>
            ) : events.length === 0 ? (
              <p className="text-sm text-slate-500">No history recorded for this carton yet.</p>
            ) : (
              <>
                <div className="space-y-3 md:hidden">
                  {events.map((event: InventoryBoxEvent) => (
                    <div key={event.id} className="rounded-lg border border-slate-200 bg-white p-3 text-sm">
                      <p className="font-medium text-slate-900">{formatEventWhen(event.created_at)}</p>
                      <p className="text-slate-600">{event.actor_display_name?.trim() || "operator"}</p>
                      <p className="mt-1 text-slate-800">{inventoryBoxEventSummary(event)}</p>
                      {event.mv_document_id != null ? (
                        <p className="mt-1 tabular-nums text-slate-600">
                          Megaventory {String(event.mv_document_id)}
                        </p>
                      ) : null}
                    </div>
                  ))}
                </div>
                <div className="hidden max-h-[min(24rem,50vh)] overflow-auto md:block">
                  <table className="w-full border-collapse text-left text-sm">
                    <thead className="sticky top-0 z-10 border-b border-slate-200 bg-white">
                      <tr>
                        <th className="px-3 py-2 font-medium text-slate-600">When</th>
                        <th className="px-3 py-2 font-medium text-slate-600">Who</th>
                        <th className="px-3 py-2 font-medium text-slate-600">Change</th>
                        <th className="px-3 py-2 text-right font-medium tabular-nums text-slate-600">
                          Megaventory
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {events.map((event: InventoryBoxEvent) => (
                        <tr
                          key={event.id}
                          className="border-b border-slate-100 hover:bg-slate-50/80 hover:[&>td]:bg-slate-50/80"
                        >
                          <td className="px-3 py-2 text-slate-700">{formatEventWhen(event.created_at)}</td>
                          <td className="px-3 py-2 text-slate-700">
                            {event.actor_display_name?.trim() || "operator"}
                          </td>
                          <td className="px-3 py-2 text-slate-700">{inventoryBoxEventSummary(event)}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-slate-700">
                            {event.mv_document_id != null ? String(event.mv_document_id) : ""}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </CardBody>
        </Card>
      ) : null}
    </div>
  );
}
